<script lang="ts" setup>
import type {
  MagentoCartItemInput,
  MagentoConfigurableProductCartItemInput,
  MagentoConfigurableProduct,
  MagentoGroupedProduct,
  MagentoProduct,
} from '~layers/app/types/magento'

export interface Props {
  product: MagentoProduct | MagentoConfigurableProduct | MagentoGroupedProduct
  productName?: string
  productPrices?: Record<string, number>
  fromPrice?: Record<string, number>
}
const meta = inject('meta', ref())
const { pending } = useCart()
const { addToCart } = useAddToCart()
const hasError = ref(false)
const error = ref(null)
onClickOutside(error, () => {
  hasError.value = false
})

const selectedItems = ref<Record<string, MagentoCartItemInput | null>>({})

const props = defineProps<Props>()

const {
  attributes,
  stockDetails,
  loadStockData,
  upsellProducts,
  crosssellProducts,
  pendingStock,
} = usePDP(meta)

onMounted(() => {
  loadStockData()
})

const emit = defineEmits<{
  productPrice: { regularPrice?: number, finalPrice?: number }
}>()

const variantPrices = computed<Record<string, number>>(() => {
  return props.product?.variants?.reduce((acc: Record<string, number>, item: unknown) => {
    const typedItem = item as { product?: { variants?: Array<{ product: { sku: string, price_range?: { minimum_price?: { final_price?: { value: number } } } } }> } }
    return {
      ...acc,
      ...typedItem?.product?.variants?.reduce((variants: Record<string, number>, variant: unknown) => {
        const typedVariant = variant as { product: { sku: string, price_range?: { minimum_price?: { final_price?: { value: number } } } } }
        variants[typedVariant.product.sku] = typedVariant.product?.price_range?.minimum_price?.final_price?.value || 0
        return variants
      }, {}),
    }
  }, {}) || {}
})

const groupedPrice = computed(() => {
  return Object.values(selectedItems.value).reduce((acc: number, item: { data?: { sku?: string } }) => {
    if (props.product?.__typename === 'GroupedProduct' && item?.data?.sku) {
      acc += variantPrices.value?.[item.data.sku] || 0
    }
    return acc
  }, 0)
})

const addToCartRef = ref(null)
const addToCartVisible = ref(true)

useIntersectionObserver(
  addToCartRef,
  ([{ isIntersecting }]) => {
    addToCartVisible.value = isIntersecting
  },
  { threshold: 0 },
)

const isPastAddToCart = computed(() => !addToCartVisible.value)

const collect = (item: Record<string, MagentoCartItemInput | MagentoConfigurableProductCartItemInput | null>) => {
  selectedItems.value = {
    ...selectedItems.value,
    ...item,
  }

  emit('productPrice', productPrices.value)
}
const add = () => {
  const item = Object.values(selectedItems.value).filter(item => item)
  if (!item.length) {
    hasError.value = true
  }
  else {
    // addToCart(item, false)
    addToCart(item, {
      suppress: false,
      product: props.product.product,
      configurableOptions: props.product.configurable_options || [],
      upsell: [...upsellProducts.value, ...crosssellProducts.value],
    })

    hasError.value = false
  }
}

const productPrices = computed<{
  regularPrice?: number
  finalPrice?: number
}>(() => {
  const product = props.product
  const basePrice = {
    regularPrice: product?.price_range?.minimum_price?.regular_price?.value,
    finalPrice: product?.price_range?.minimum_price?.final_price?.value,
  }

  if (!product?.variants?.length) return basePrice

  const allUids = Object.values(selectedItems.value).flatMap(i => i?.selected_options || []).filter(Boolean)

  if (!allUids.length) return basePrice

  const uidSet = new Set(allUids)
  const variant = product?.variants.find((variantItem) => {
    const attrs = variantItem?.attributes
    return Array.isArray(attrs) && attrs.length && attrs.every(attributes => uidSet.has(attributes?.uid))
  })

  const range = variant?.product?.price_range?.minimum_price
  return {
    regularPrice: range?.regular_price?.value ?? basePrice.regularPrice,
    finalPrice: range?.final_price?.value ?? basePrice.finalPrice,
  }
})
</script>

<template>
  <div
    ref="addToCartRef"
    class="add-to-cart"
  >
    <ProductSelector
      :product="props.product"
      @selected="collect"
    />

    <span
      v-if="hasError"
      ref="error"
      class="required"
    >{{ $t('Please choose a size') }}</span>

    <ProductOfferButton
      v-if="product.product.isOffer"
      class="product-item__offer-button"
      :product="product.product"
      :label="$t('Request a free quote')"
    />
    <button
      v-else
      type="button"
      :disabled="pending || pendingStock"
      class="add-to-cart__cta"
      data-testid="add-to-cart"
      @click.prevent="add"
    >
      <span>{{ $t('Add to cart') }}</span>
      <span v-if="groupedPrice > 0">-</span>
      <i18n-n
        v-if="groupedPrice > 0"
        tag="span"
        :value="groupedPrice"
        :format="{ style: 'currency', currency: 'EUR' }"
      >
        <template #currency="slotProps">
          <span class="price__currency">{{ slotProps.currency }}</span>
        </template>
        <template #integer="slotProps">
          <span class="price__integer">{{ slotProps.integer }}</span>
        </template>
        <template #decimal="slotProps">
          <span class="price__decimal">{{ slotProps.decimal }}</span>
        </template>
        <template #fraction="slotProps">
          <span class="price__fraction">{{ slotProps.fraction }}</span>
        </template>
      </i18n-n>
    </button>

    <ProductViewInStore
      v-if="attributes.poo_in_store === 'Ja'"
      :data="stockDetails"
    />

    <ClientOnly>
      <Teleport
        defer
        to="#teleports"
      >
        <!--  -->
        <Transition name="slide">
          <div
            v-if="isPastAddToCart"
            class="pdp__sticky-add-to-cart"
          >
            <div class="pdp__sticky-add-to-cart--container">
              <div class="pdp__sticky-add-to-cart--header">
                {{ $t(productName || '') }}
              </div>

              <div class="pdp__sticky-add-to-cart--pricing pdp__pricing">
                <div v-if="fromPrice">
                  <UiPrice
                    v-if="fromPrice"
                    :prices="fromPrice"
                    class="product-info__from-price"
                  >
                    <template #prefix>
                      <span>{{ $t('From:') }}</span>
                    </template>
                  </UiPrice>
                </div>
                <UiPrice
                  v-else-if="productPrices"
                  :prices="productPrices"
                  class="product-info__price"
                />
              </div>

              <div class="pdp__sticky-add-to-cart--button">
                <ProductOfferButton
                  v-if="product.product.isOffer"
                  class="product-item__offer-button"
                  :product="product.product"
                  :label="$t('Free quote')"
                />
                <button
                  v-else
                  type="button"
                  :disabled="pending || pendingStock"
                  class="add-to-cart__cta"
                  data-testid="add-to-cart"
                  @click.prevent="add"
                >
                  <span>{{ $t('Add to cart') }}</span>
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style>
  @import url("~layers/app/components/Product/AddToCart.css");
</style>
